export default defineNuxtRouteMiddleware(async to => {
  // Prevent further authentication attempts in case the user has blocked cookies
  // Blocking cookies also blocks localStorage and sessionStorage, breaking our platform
  if (import.meta.client && !navigator.cookieEnabled) return

  const { $sentry } = useNuxtApp()

  const userStore = useUserStore()
  const businessStore = useBusinessStore()

  const { logError } = useErrorHandling()

  const redirectUrl = useCookie('redirectUrl')

  const accessControl = {
    featureFlags: new Set(to.meta.accessControl?.featureFlags ?? []),
    roles: new Set(to.meta.accessControl?.roles ?? []),
    isStrict: to.meta.accessControl?.isStrict ?? false,
  } satisfies { featureFlags: Set<FeatureFlagName>; roles: Set<AppRole>; isStrict: boolean }

  try {
    if (accessControl.roles.size === 1 && accessControl.roles.has('unauthenticated')) {
      return
    }
    if (accessControl.roles.size === 1 && accessControl.roles.has('nas')) {
      const directSignStore = useDirectSignStore()

      if (directSignStore.isDirectSign) {
        // We already have a direct sign session, no need to re-authenticate
        return
      }

      // NAS entry points will construct auth information from the URL parameters
      // for CIBA flow, the redirect URL we receive will contain userid and id as
      // query params, so I added a fallback for them here
      const userid = to.params.userid ?? to.query.userid
      const id = to.params.id ?? to.query.id

      if (typeof userid !== 'string' || typeof id !== 'string') {
        // Invalid direct sign parameters
        return navigateTo('/')
      }

      directSignStore.setDirectSign(userid, id)
      await userStore.fetchUser()
      $sentry?.setUser({ id: userid })

      return
    }

    try {
      await userStore.ensureUser()
      await businessStore.ensureBusiness()

      if ('registrationType' in userStore.attributes) {
        if (
          userStore.attributes.registrationType === 'business' &&
          !businessStore.id &&
          to.path !== '/business/signup/steps'
        ) {
          redirectUrl.value = '/business/signup/steps'
        } else if (
          userStore.attributes.registrationType === 'starter' &&
          !businessStore.id &&
          to.path !== '/welcome' &&
          // /welcome sends a failed starter conversion here; keep the registrationType so the next navigation retries
          to.path !== '/sorry'
        ) {
          redirectUrl.value = '/welcome'
        } else if (userStore.attributes.registrationType === 'default' && to.path !== '/welcome') {
          redirectUrl.value = '/welcome'
        }
      }

      $sentry?.setUser({ id: userStore.id })
    } catch (error) {
      if (isFetchError(error) && isGatewayUnauthResponse(error.response)) {
        if (!accessControl.roles.has('unauthenticated')) {
          return navigateTo({ name: 'login' })
        }
      } else if (isFetchError(error) && isGatewaySessionRevokedResponse(error.response)) {
        // The HTTP layer has already started the logout redirect
        return abortNavigation()
      } else {
        logError('Failed to fetch user data', error)
      }
    }

    const { $unleash } = useNuxtApp()

    // Ensure fresh feature flag status
    await $unleash.client?.updateToggles()

    const isMissingFeatureFlag = [...accessControl.featureFlags].some(flagName => !$unleash.client?.isEnabled(flagName))

    const userRoles = accessControl.isStrict ? new Set([userStore.role]) : userStore.accessRoles

    if (accessControl.roles.isDisjointFrom(userRoles) || isMissingFeatureFlag) {
      // Access mismatch, redirect to appropriate fallback
      return navigateTo({ name: '404' })
    }

    if (redirectUrl.value) {
      const url = redirectUrl.value
      redirectUrl.value = null

      if (!url.startsWith('/login') && !url.startsWith('/logout')) {
        return navigateTo(url)
      }
    }
  } catch (error) {
    logError('Failed to authenticate user', error)
  }
})
